import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Square, 
  RefreshCw, 
  Eye, 
  Terminal, 
  Calendar, 
  Clock, 
  User, 
  X, 
  AlertCircle, 
  CheckCircle,
  Pause,
  RotateCcw,
  Search,
  Filter,
  MoreVertical,
  Activity,
  Server,
  Cpu,
  HardDrive,
  Zap,
  TrendingUp,
  FileText,
  Download,
  Grid,
  List
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Job, JobStatusResponse, CancelJobResponse } from './types';

const JobManagement: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // 模拟作业数据
  const initialJobs: Job[] = [
    {
      id: '22',
      name: '分子动力学模拟-蛋白质折叠',
      status: 'RUNNING',
      user: '张三',
      submitTime: '2024-01-15 09:30:00',
      startTime: '2024-01-15 09:32:00',
      nodes: 4,
      cores: 128,
      memory: 512,
      app: 'GROMACS',
      progress: 65,
      priority: 'high',
      masterId: '602ce21cf5c8360001815ac5',
      requestId: 'req_1705294200000',
      estimatedTime: '6h 30m'
    },
    {
      id: '23',
      name: 'CNN模型训练-图像识别',
      status: 'COMPLETED',
      user: '李四',
      submitTime: '2024-01-15 08:15:00',
      startTime: '2024-01-15 08:17:00',
      endTime: '2024-01-15 12:45:00',
      nodes: 2,
      cores: 64,
      memory: 256,
      app: 'TensorFlow',
      priority: 'normal',
      masterId: '602ce21cf5c8360001815ac5',
      requestId: 'req_1705289700000'
    },
    {
      id: '24',
      name: '流体力学仿真-湍流分析',
      status: 'PENDING',
      user: '王五',
      submitTime: '2024-01-15 10:20:00',
      nodes: 8,
      cores: 256,
      memory: 1024,
      app: 'OpenFOAM',
      priority: 'urgent',
      masterId: '602ce21cf5c8360001815ac5',
      requestId: 'req_1705297200000',
      estimatedTime: '12h 15m'
    },
    {
      id: '25',
      name: '量子化学计算-分子轨道',
      status: 'FAILED',
      user: '赵六',
      submitTime: '2024-01-15 07:45:00',
      startTime: '2024-01-15 07:47:00',
      endTime: '2024-01-15 08:12:00',
      nodes: 1,
      cores: 32,
      memory: 128,
      app: 'Gaussian',
      priority: 'low',
      masterId: '602ce21cf5c8360001815ac5',
      requestId: 'req_1705287900000'
    }
  ];

  useEffect(() => {
    setJobs(initialJobs);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'FAILED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return <Activity className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4" />;
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      case 'FAILED':
        return <AlertCircle className="w-4 h-4" />;
      case 'CANCELLED':
        return <X className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'normal':
        return 'bg-blue-500';
      case 'low':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesStatus = filterStatus === 'all' || job.status === filterStatus;
    const matchesSearch = job.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.app.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const refreshJobs = async () => {
    setIsLoading(true);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      setJobs([...initialJobs]);
      toast.success('作业列表已刷新');
    } catch (error) {
      toast.error('刷新失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelJob = async (jobId: string) => {
    try {
      // 模拟取消作业API调用
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, status: 'CANCELLED' as const } : job
      ));
      
      toast.success('作业已取消');
    } catch (error) {
      toast.error('取消作业失败');
    }
  };

  const StatusFilters = () => (
    <div className="flex items-center space-x-2 overflow-x-auto">
      {[
        { key: 'all', label: '全部', count: jobs.length },
        { key: 'RUNNING', label: '运行中', count: jobs.filter(j => j.status === 'RUNNING').length },
        { key: 'COMPLETED', label: '已完成', count: jobs.filter(j => j.status === 'COMPLETED').length },
        { key: 'PENDING', label: '排队中', count: jobs.filter(j => j.status === 'PENDING').length },
        { key: 'FAILED', label: '失败', count: jobs.filter(j => j.status === 'FAILED').length }
      ].map(filter => (
        <button
          key={filter.key}
          onClick={() => setFilterStatus(filter.key)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
            filterStatus === filter.key
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-white/70 text-gray-700 hover:bg-white hover:shadow-md'
          }`}
        >
          <span>{filter.label}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${
            filterStatus === filter.key
              ? 'bg-white/20 text-white'
              : 'bg-gray-200 text-gray-600'
          }`}>
            {filter.count}
          </span>
        </button>
      ))}
    </div>
  );

  const JobCard = ({ job }: { job: Job }) => (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
      {/* 优先级指示条 */}
      <div className={`h-1 ${getPriorityColor(job.priority)}`} />
      
      <div className="p-6">
        {/* 头部 */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
              {job.name}
            </h3>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span>{job.user}</span>
              <span>•</span>
              <span>{job.app}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
              {getStatusIcon(job.status)}
              <span>{job.status}</span>
            </span>
            
            <div className="relative">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* 进度条 (仅运行中的作业显示) */}
        {job.status === 'RUNNING' && job.progress && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>执行进度</span>
              <span>{job.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${job.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* 资源信息 */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Server className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-sm font-medium text-gray-900">{job.nodes}</div>
            <div className="text-xs text-gray-600">节点</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Cpu className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-sm font-medium text-gray-900">{job.cores}</div>
            <div className="text-xs text-gray-600">核心</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <HardDrive className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-sm font-medium text-gray-900">{job.memory}GB</div>
            <div className="text-xs text-gray-600">内存</div>
          </div>
        </div>

        {/* 时间信息 */}
        <div className="text-xs text-gray-600 mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3" />
              <span>提交: {new Date(job.submitTime).toLocaleString()}</span>
            </div>
            {job.estimatedTime && (
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>预计: {job.estimatedTime}</span>
              </div>
            )}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setSelectedJob(job);
                setShowJobDetails(true);
              }}
              className="flex items-center space-x-1 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm"
            >
              <Eye className="w-4 h-4" />
              <span>详情</span>
            </button>
            
            <button className="flex items-center space-x-1 px-3 py-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-200 text-sm">
              <Terminal className="w-4 h-4" />
              <span>日志</span>
            </button>
          </div>

          {job.status === 'RUNNING' && (
            <button
              onClick={() => cancelJob(job.id)}
              className="flex items-center space-x-1 px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm"
            >
              <Square className="w-4 h-4" />
              <span>取消</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 portrait:p-4">
      {/* 顶部标题区 */}
      <div className="max-w-7xl mx-auto mb-8 portrait:mb-5">
        <div className="flex items-center justify-between portrait:flex-col portrait:items-start portrait:space-y-3">
          <div className="flex items-center space-x-5 portrait:space-x-4">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 portrait:w-12 portrait:h-12 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Activity className="w-7 h-7 portrait:w-6 portrait:h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 portrait:w-4 portrait:h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-2.5 h-2.5 portrait:w-2 portrait:h-2 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl portrait:text-xl portrait:md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                作业管理
              </h1>
              <p className="text-gray-600 mt-2 portrait:mt-1 text-base portrait:text-sm leading-relaxed">管理和监控您的计算作业</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 portrait:space-x-2 portrait:w-full portrait:justify-start">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center space-x-2 portrait:space-x-1 px-4 py-2 portrait:px-3 portrait:py-1.5 bg-white/70 backdrop-blur-sm hover:bg-white border border-white/50 rounded-xl portrait:rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm portrait:text-xs"
            >
              {viewMode === 'grid' ? <List className="w-4 h-4 portrait:w-3 portrait:h-3" /> : <Grid className="w-4 h-4 portrait:w-3 portrait:h-3" />}
              <span>{viewMode === 'grid' ? '列表' : '网格'}</span>
            </button>
            
            <button
              onClick={refreshJobs}
              disabled={isLoading}
              className="flex items-center space-x-2 portrait:space-x-1 px-4 py-2 portrait:px-3 portrait:py-1.5 bg-white/70 backdrop-blur-sm hover:bg-white border border-white/50 rounded-xl portrait:rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm portrait:text-xs"
            >
              <RefreshCw className={`w-4 h-4 portrait:w-3 portrait:h-3 ${isLoading ? 'animate-spin' : ''}`} />
              <span>刷新</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* 搜索和筛选区 */}
        <div className="mb-8 portrait:mb-5 space-y-4 portrait:space-y-3">
          {/* 搜索框 */}
          <div className="relative">
            <Search className="absolute left-4 portrait:left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 portrait:w-4 portrait:h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索作业名称、用户或应用..."
              className="w-full pl-12 portrait:pl-10 pr-4 portrait:pr-3 py-3 portrait:py-2 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl portrait:rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-base portrait:text-sm"
            />
          </div>

          {/* 状态筛选 */}
          <StatusFilters />
        </div>

        {/* 作业列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">加载中...</span>
            </div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <FileText className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无作业</h3>
            <p className="text-gray-600 mb-4">没有找到符合条件的作业</p>
          </div>
        ) : (
          <div className={`${
            viewMode === 'grid' 
              ? 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6'
              : 'space-y-0'
          }`}>
            {viewMode === 'list' ? (
              // 列表视图
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed min-w-[1400px]">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="w-[320px] px-6 py-4 text-left text-sm font-semibold text-gray-900">作业名称</th>
                        <th className="w-[140px] px-6 py-4 text-center text-sm font-semibold text-gray-900">状态</th>
                        <th className="w-[100px] px-6 py-4 text-center text-sm font-semibold text-gray-900">用户</th>
                        <th className="w-[120px] px-6 py-4 text-center text-sm font-semibold text-gray-900">应用</th>
                        <th className="w-[140px] px-6 py-4 text-center text-sm font-semibold text-gray-900">资源</th>
                        <th className="w-[180px] px-6 py-4 text-center text-sm font-semibold text-gray-900">提交时间</th>
                        <th className="w-[140px] px-6 py-4 text-center text-sm font-semibold text-gray-900">进度</th>
                        <th className="w-[160px] px-6 py-4 text-center text-sm font-semibold text-gray-900">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200/50">
                      {filteredJobs.map(job => (
                        <tr key={job.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-8 rounded-full ${getPriorityColor(job.priority)}`} />
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900 truncate">{job.name}</div>
                                <div className="text-sm text-gray-500">ID: {job.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                              {getStatusIcon(job.status)}
                              <span>{job.status}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm text-gray-900 font-medium">{job.user}</div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm text-gray-900 font-medium">{job.app}</div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm text-gray-900 font-medium">
                              <div>{job.nodes} 节点</div>
                              <div className="text-xs text-gray-500">{job.cores} 核心</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm text-gray-900 font-medium">
                              {new Date(job.submitTime).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(job.submitTime).toLocaleTimeString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {job.status === 'RUNNING' && job.progress ? (
                              <div className="flex items-center justify-center space-x-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${job.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 font-medium">{job.progress}%</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedJob(job);
                                  setShowJobDetails(true);
                                }}
                                className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 font-medium"
                              >
                                查看
                              </button>
                              {job.status === 'RUNNING' && (
                                <button className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 font-medium">
                                  停止
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              // 网格视图
              filteredJobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))
            )}
          </div>
        )}
      </div>

      {/* 作业详情弹窗 */}
      {showJobDetails && selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">作业详情</h2>
                <button
                  onClick={() => setShowJobDetails(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">基本信息</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">作业名称:</span>
                    <span className="ml-2 font-medium">{selectedJob.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">状态:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(selectedJob.status)}`}>
                      {selectedJob.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">用户:</span>
                    <span className="ml-2 font-medium">{selectedJob.user}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">应用:</span>
                    <span className="ml-2 font-medium">{selectedJob.app}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">资源配置</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">节点数:</span>
                    <span className="ml-2 font-medium">{selectedJob.nodes}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">CPU核心:</span>
                    <span className="ml-2 font-medium">{selectedJob.cores}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">内存:</span>
                    <span className="ml-2 font-medium">{selectedJob.memory} GB</span>
                  </div>
                  <div>
                    <span className="text-gray-600">优先级:</span>
                    <span className="ml-2 font-medium">{selectedJob.priority}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">时间信息</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">提交时间:</span>
                    <span className="ml-2 font-medium">{selectedJob.submitTime}</span>
                  </div>
                  {selectedJob.startTime && (
                    <div>
                      <span className="text-gray-600">开始时间:</span>
                      <span className="ml-2 font-medium">{selectedJob.startTime}</span>
                    </div>
                  )}
                  {selectedJob.endTime && (
                    <div>
                      <span className="text-gray-600">结束时间:</span>
                      <span className="ml-2 font-medium">{selectedJob.endTime}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;
